/**
 * Everything a contact is exchanging, grouped by the representative holding it. D-25.
 *
 * The screen that did not exist before, because a session named one exchange and there was nothing
 * to list. It exists now for one reason the user's own words gave: a person asked for documents by
 * two representatives has to be able to tell which ask is which. So the grouping is by rep and not
 * by date, and the rep's name is a heading rather than a line of metadata under a title - a title
 * like "Onboarding" is not distinguishing when both of them say it.
 *
 * Within a group the server's order is kept, which is most recently touched first.
 *
 * Nothing here is a link element. Each row is a button, because opening one is a state change in a
 * surface with no router (see `App.tsx`) rather than a navigation, and an anchor with no `href` is
 * the thing screen readers announce as a link that goes nowhere.
 */
import { Button, Card, Page, Row } from '@iqz-ddds/components';
import { FailureNotice } from '../components/FailureNotice';
import type { GuestExchangeSummary } from '@iqz-ddds/contracts';

export interface ExchangeListScreenProps {
  readonly contactName: string;
  readonly exchanges: readonly GuestExchangeSummary[];
  readonly onOpen: (exchangeId: string) => void;
  /**
   * A failed open, shown here because there is nowhere else for it to go.
   *
   * Found by driving this in a browser: opening an exchange that answered an error left the
   * list on screen with nothing said, so the button read as broken. `App` was catching the
   * failure into state and this screen was not rendering it - which is DG.14's shape exactly,
   * a surface that knows something went wrong and says nothing.
   */
  readonly error?: unknown;
}

/** Group order follows first appearance, so the most recently touched rep is first. */
function byRep(exchanges: readonly GuestExchangeSummary[]): { rep: string; items: GuestExchangeSummary[] }[] {
  const groups: { rep: string; items: GuestExchangeSummary[] }[] = [];
  for (const exchange of exchanges) {
    const existing = groups.find((group) => group.rep === exchange.repName);
    if (existing === undefined) groups.push({ rep: exchange.repName, items: [exchange] });
    else existing.items.push(exchange);
  }
  return groups;
}

/**
 * "2 documents needed" rather than "2 open requests".
 *
 * The person reading this did not file a request and will not recognise the word. DG.18a is the
 * same complaint from the representative's side.
 */
function waitingOn(count: number): string | null {
  if (count === 0) return null;
  return count === 1 ? '1 document needed' : `${count} documents needed`;
}

export function ExchangeListScreen({ contactName, exchanges, onOpen, error }: ExchangeListScreenProps): React.JSX.Element {
  if (exchanges.length === 0) {
    return (
      <Page eyebrow="Secure documents" title="Nothing here yet">
        {/* Reachable: a contact can exist before any work starts. Not an error, and it must not read
            like one - the sign-in that got them here cannot say whether an address is known, so a
            page that said "no account" would give away the thing the sign-in withheld. */}
        <p className="ddds-empty">
          When your adviser asks you for a document or sends you one, it will appear here.
        </p>
      </Page>
    );
  }

  return (
    <Page eyebrow="Secure documents" title={`Your documents, ${contactName}`}>
      <FailureNotice error={error} />

      {byRep(exchanges).map((group) => (
        <Card key={group.rep} title={group.rep}>
          <ul className="ddds-list">
            {group.items.map((exchange) => (
              <Row
                key={exchange.exchangeId}
                name={exchange.title}
                meta={waitingOn(exchange.openRequestCount) ?? 'Nothing needed from you'}
                action={
                  <Button size="small" variant="quiet" onClick={() => onOpen(exchange.exchangeId)}>
                    Open
                  </Button>
                }
              />
            ))}
          </ul>
        </Card>
      ))}
    </Page>
  );
}
